import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import api from "../../api/config";
import { toast } from "react-toastify";
import {
  Form,
  Input,
  Button,
  Upload,
  Select,
  Card,
  Typography,
  Divider,
  Row,
  Col,
  message,
  Space,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  LockOutlined,
  PlusOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  TeamOutlined,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ConsultantRegister = ({ editingConsultant = null, isAdminEdit = false, onSuccess, isModal = false }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [profilePictureList, setProfilePictureList] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [industriesLoading, setIndustriesLoading] = useState(false);

  const navigate = useNavigate();

  // Fetch industries on component mount
  useEffect(() => {
    const loadIndustries = async () => {
      try {
        setIndustriesLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/industries`);
        
        if (response && response.data) {
          if (response.data.success && response.data.data) {
            setIndustries(response.data.data);
          } else if (response.data.industries) {
            setIndustries(response.data.industries);
          } else if (Array.isArray(response.data)) {
            setIndustries(response.data);
          }
        }
      } catch (error) {
        console.error("Error fetching industries:", error);
        setIndustries([]);
      } finally {
        setIndustriesLoading(false);
      }
    };

    loadIndustries();
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (editingConsultant && isAdminEdit && form) {
      form.setFieldsValue({
        fullName: editingConsultant.fullName || "",
        email: editingConsultant.email || "",
        phone: editingConsultant.phone || "",
        expertise: editingConsultant.expertise || "",
        yearsOfExperience: editingConsultant.yearsOfExperience || 0,
        consultingFields: editingConsultant.consultingFields || [],
        description: editingConsultant.description || "",
        website: editingConsultant.website || "",
        country: editingConsultant.country || "",
        state: editingConsultant.state || "",
        city: editingConsultant.city || "",
        linkedinProfile: editingConsultant.linkedinProfile || "",
      });

      // Set profile picture preview
      if (editingConsultant.profilePicture) {
        let imageUrl = editingConsultant.profilePicture;
        if (!imageUrl.startsWith('http')) {
          if (imageUrl.startsWith('/')) {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${imageUrl}`;
          } else {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/${imageUrl}`;
          }
        }
        setProfilePictureList([{
          uid: '-1',
          name: 'profile.png',
          status: 'done',
          url: imageUrl,
        }]);
      }
    }
  }, [editingConsultant, isAdminEdit, form]);

  const baseStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#333",
  };

  const inputStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "15px",
    borderRadius: "6px",
  };

  const labelStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "16px",
    fontWeight: 400,
    color: "#333",
    marginBottom: "6px",
  };

  const onFinish = async (values) => {
    setLoading(true);

    try {
      const formData = new FormData();

      // Append all text fields
      formData.append("fullName", values.fullName);
      formData.append("email", values.email);
      formData.append("phone", values.phone);
      
      // Skip password when editing
      if (!isAdminEdit) {
        formData.append("password", values.password);
      }
      
      formData.append("expertise", values.expertise);
      formData.append("yearsOfExperience", values.yearsOfExperience);
      formData.append("consultingFields", JSON.stringify(values.consultingFields || []));
      formData.append("description", values.description || "");
      formData.append("website", values.website || "");
      formData.append("country", values.country);
      formData.append("state", values.state);
      formData.append("city", values.city);
      formData.append("linkedinProfile", values.linkedinProfile || "");

      // Append profile picture if exists and is new
      if (profilePictureList.length > 0 && profilePictureList[0].originFileObj) {
        formData.append("profilePicture", profilePictureList[0].originFileObj);
      }

      if (isAdminEdit && editingConsultant) {
        // Update existing consultant
        const response = await api.put(`/admin/consultants/${editingConsultant._id}`, formData);
        
        if (response.data.success) {
          toast.success("Consultant updated successfully!");
          if (onSuccess) onSuccess();
        }
      } else {
        // Register new consultant
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/auth/consultant-register`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data.success) {
          message.success("Registration submitted successfully! Awaiting admin approval.");
          form.resetFields();
          setProfilePictureList([]);

          if (onSuccess) {
            onSuccess();
          } else if (!isModal) {
            navigate("/login");
          }
        }
      }
    } catch (error) {
      console.error("Registration/Update error:", error);
      const errorMsg = error.response?.data?.message || "Operation failed. Please try again.";
      if (isAdminEdit) {
        toast.error(errorMsg);
      } else {
        message.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureChange = ({ fileList: newFileList }) => {
    setProfilePictureList(newFileList);
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
      return Upload.LIST_IGNORE;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image must be smaller than 5MB!");
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <div style={{ padding: isModal ? "0" : "20px", maxWidth: "800px", margin: "0 auto" }}>
      {!isModal && (
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <Title level={2} style={{ ...baseStyle, fontSize: "28px", fontWeight: 500, color: "#470172" }}>
            Consultant Registration
          </Title>
          <Text style={{ ...baseStyle, fontSize: "16px", opacity: 0.7 }}>
            Register as a consultant or advisory professional
          </Text>
        </div>
      )}

      <Form
        form={form}
        name="consultant_register"
        onFinish={onFinish}
        layout="vertical"
        requiredMark="optional"
        scrollToFirstError
      >
        {/* Profile Picture */}
        <Form.Item
          label={<span style={labelStyle}>Profile Picture</span>}
          style={{ marginBottom: "20px" }}
        >
          <Upload
            listType="picture-card"
            fileList={profilePictureList}
            onChange={handleProfilePictureChange}
            beforeUpload={beforeUpload}
            maxCount={1}
          >
            {profilePictureList.length === 0 && uploadButton}
          </Upload>
          <Text style={{ fontSize: "12px", color: "#999" }}>
            Upload a professional profile picture (Max 5MB)
          </Text>
        </Form.Item>

        <Divider style={{ margin: "24px 0" }} />

        {/* Personal Information */}
        <Title level={4} style={{ ...baseStyle, fontSize: "18px", fontWeight: 500, marginBottom: "16px", color: "#470172" }}>
          Personal Information
        </Title>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="fullName"
              label={<span style={labelStyle}>Full Name</span>}
              rules={[{ required: true, message: "Please enter your full name" }]}
            >
              <Input
                prefix={<UserOutlined style={{ color: "#470172" }} />}
                placeholder="John Doe"
                style={inputStyle}
                size="large"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="email"
              label={<span style={labelStyle}>Email</span>}
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input
                prefix={<MailOutlined style={{ color: "#470172" }} />}
                placeholder="john@example.com"
                style={inputStyle}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="phone"
              label={<span style={labelStyle}>Phone</span>}
              rules={[{ required: true, message: "Please enter your phone number" }]}
            >
              <Input
                prefix={<PhoneOutlined style={{ color: "#470172" }} />}
                placeholder="+91 9876543210"
                style={inputStyle}
                size="large"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="website"
              label={<span style={labelStyle}>Website (Optional)</span>}
            >
              <Input
                prefix={<GlobalOutlined style={{ color: "#470172" }} />}
                placeholder="https://yourwebsite.com"
                style={inputStyle}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        {!isAdminEdit && (
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="password"
                label={<span style={labelStyle}>Password</span>}
                rules={[
                  { required: true, message: "Please enter a password" },
                  { min: 6, message: "Password must be at least 6 characters" },
                ]}
                hasFeedback
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: "#470172" }} />}
                  placeholder="Enter password"
                  style={inputStyle}
                  size="large"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="confirmPassword"
                label={<span style={labelStyle}>Confirm Password</span>}
                dependencies={["password"]}
                hasFeedback
                rules={[
                  { required: true, message: "Please confirm your password" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("Passwords do not match!"));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: "#470172" }} />}
                  placeholder="Confirm password"
                  style={inputStyle}
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Divider style={{ margin: "24px 0" }} />

        {/* Professional Information */}
        <Title level={4} style={{ ...baseStyle, fontSize: "18px", fontWeight: 500, marginBottom: "16px", color: "#470172" }}>
          Professional Information
        </Title>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="expertise"
              label={<span style={labelStyle}>Primary Expertise</span>}
              rules={[{ required: true, message: "Please enter your expertise" }]}
            >
              <Input
                placeholder="e.g., Business Strategy, Marketing"
                style={inputStyle}
                size="large"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="yearsOfExperience"
              label={<span style={labelStyle}>Years of Experience</span>}
              rules={[{ required: true, message: "Please enter years of experience" }]}
            >
              <Input
                type="number"
                placeholder="10"
                style={inputStyle}
                size="large"
                min={0}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="consultingFields"
          label={<span style={labelStyle}>Consulting Fields</span>}
          rules={[{ required: true, message: "Please select at least one consulting field" }]}
        >
          <Select
            mode="multiple"
            placeholder="Select consulting fields"
            style={inputStyle}
            size="large"
            loading={industriesLoading}
          >
            {industries.map((industry) => (
              <Option key={industry._id || industry.id} value={industry.sectorName || industry.name}>
                {industry.sectorName || industry.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="description"
          label={<span style={labelStyle}>About You</span>}
          rules={[{ required: true, message: "Please provide a description" }]}
        >
          <TextArea
            rows={4}
            placeholder="Describe your consulting experience, specializations, and what you offer..."
            style={inputStyle}
          />
        </Form.Item>

        <Form.Item
          name="linkedinProfile"
          label={<span style={labelStyle}>LinkedIn Profile (Optional)</span>}
        >
          <Input
            prefix={<TeamOutlined style={{ color: "#470172" }} />}
            placeholder="https://linkedin.com/in/yourprofile"
            style={inputStyle}
            size="large"
          />
        </Form.Item>

        <Divider style={{ margin: "24px 0" }} />

        {/* Location Information */}
        <Title level={4} style={{ ...baseStyle, fontSize: "18px", fontWeight: 500, marginBottom: "16px", color: "#470172" }}>
          Location
        </Title>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="country"
              label={<span style={labelStyle}>Country</span>}
              rules={[{ required: true, message: "Please enter your country" }]}
            >
              <Input
                prefix={<EnvironmentOutlined style={{ color: "#470172" }} />}
                placeholder="India"
                style={inputStyle}
                size="large"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="state"
              label={<span style={labelStyle}>State</span>}
              rules={[{ required: true, message: "Please enter your state" }]}
            >
              <Input
                placeholder="Maharashtra"
                style={inputStyle}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              name="city"
              label={<span style={labelStyle}>City</span>}
              rules={[{ required: true, message: "Please enter your city" }]}
            >
              <Input
                placeholder="Mumbai"
                style={inputStyle}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Submit Button */}
        <Form.Item style={{ marginTop: "32px", textAlign: "center" }}>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={loading}
            style={{
              fontFamily: "Rubik, sans-serif",
              fontSize: "16px",
              fontWeight: 500,
              height: "48px",
              width: "100%",
              maxWidth: "300px",
              backgroundColor: "#470172",
              borderColor: "#470172",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(71, 1, 114, 0.25)",
            }}
          >
            {loading ? "Submitting..." : "Register"}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default ConsultantRegister;
